/**
 * PortfolioGlance
 * Unified portfolio column: alert positions first, then healthy positions.
 * Always fills column height with consistent layout.
 */

import React, { useMemo } from 'react';
import { RuleGlyph } from '../common/Glyphs';
import { useNavigate } from 'react-router-dom';
import { formatSignedCurrency } from '../../utils/formatters';
import { daysUntilEarnings } from '../../utils/earnings';

interface Position {
  id: string;
  ticker: string;
  strikePrice: number;
  expiryDate: string;
  numContracts: number;
  daysToExpiration?: number | null;
  // Gold v2 phase 3b: the row's gold figures (gold.v_position)
  totalPnl?: number | null;
  todayPnl?: number | null;
  earningsInside?: boolean | null;
  // Discipline v3 verdict (gold.position_rule_state) — the same verdict the
  // position rows show. Pro only; Free viewers get none.
  disciplineAction?: string | null;
  nextEarningsDate?: string | null;
}

// Verdicts that DON'T need attention — mirrors the actionsRequired count in
// usePortfolioKPILoader so the hero list and count stay in lockstep.
const PASSIVE_DISCIPLINE = new Set(['Hold', 'Stale Data', 'Insufficient Data']);

// Severity rank for sorting alerts (red exits first, then actionable, then earnings).
const RED_ACTIONS = new Set(['Close at Loss', 'Close at Debit', 'Close Both']);

interface PortfolioGlanceProps {
  activePositions: Position[];
  loading: boolean;
  onNavigateToScreener: () => void;
}

const formatExpiry = (expiryDate: string, dte?: number | null) => {
  try {
    const date = new Date(expiryDate);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const suffix = dte !== undefined && dte !== null ? ` (${dte}d)` : '';
    return `${months[date.getMonth()]} ${date.getDate()}${suffix}`;
  } catch {
    return expiryDate;
  }
};

// Held-position earnings risk: the report lands before the option expires
// (gold earnings_inside, the same test as the position row and the engine).
const getEarningsWarning = (
  nextEarningsDate: string | null | undefined,
  inside: boolean | null | undefined,
): { daysUntil: number; label: string } | null => {
  if (!inside) return null;
  const daysUntil = daysUntilEarnings(nextEarningsDate ?? undefined);
  if (daysUntil == null || daysUntil < 0) return null;
  const label = daysUntil === 0 ? 'Earnings TODAY' : daysUntil === 1 ? 'Earnings Tmrw' : `Earnings in ${daysUntil}d`;
  return { daysUntil, label };
};

// Resolve the status chip a position should show in the "Needs Attention" list:
// the Discipline verdict (Pro). Returns null when the position is on track on
// its own merits (earnings is handled separately by the caller).
const getAlertDisplay = (
  pos: Position,
): { label: string; isRed: boolean } | null => {
  const da = pos.disciplineAction;
  if (da != null) {
    if (PASSIVE_DISCIPLINE.has(da)) return null;
    const isRed = RED_ACTIONS.has(da);
    return { label: da, isRed };
  }
  return null;
};

const PortfolioGlance: React.FC<PortfolioGlanceProps> = ({
  activePositions,
  loading,
  onNavigateToScreener,
}) => {
  const navigate = useNavigate();

  // Group by ticker, split alert vs healthy, sort red before yellow
  const { alertPositions, healthyPositions } = useMemo(() => {
    if (!activePositions || activePositions.length === 0) {
      return { alertPositions: [] as Position[], healthyPositions: [] as Position[] };
    }

    const alerts: Position[] = [];
    const healthy: Position[] = [];

    // Group by ticker for dedup display
    const seen = new Map<string, Position[]>();
    activePositions.forEach(pos => {
      const group = seen.get(pos.ticker) || [];
      group.push(pos);
      seen.set(pos.ticker, group);
    });

    // A position needs attention when its Discipline verdict is actionable
    // (getAlertDisplay non-null) OR an earnings print lands before expiry.
    const needsAttention = (p: Position): boolean =>
      getAlertDisplay(p) != null || getEarningsWarning(p.nextEarningsDate, p.earningsInside) != null;

    // For each ticker group, check if any position needs attention
    seen.forEach((positions, ticker) => {
      const hasAlert = positions.some(needsAttention);

      if (hasAlert) {
        // Show individual positions that need attention
        positions.forEach(p => {
          if (needsAttention(p)) {
            alerts.push(p);
          } else {
            healthy.push(p);
          }
        });
      } else {
        healthy.push(...positions);
      }
    });

    // Sort alerts: red exits first, then other actionable verdicts, then
    // earnings-only rows; tie-break by absolute P&L.
    const rank = (p: Position): number => {
      const d = getAlertDisplay(p);
      if (d) return d.isRed ? 0 : 1;
      return 2; // earnings-only
    };
    alerts.sort((a, b) => {
      const pa = rank(a);
      const pb = rank(b);
      if (pa !== pb) return pa - pb;
      return Math.abs(b.totalPnl ?? 0) - Math.abs(a.totalPnl ?? 0);
    });

    // Sort healthy by ticker
    healthy.sort((a, b) => a.ticker.localeCompare(b.ticker));

    return { alertPositions: alerts, healthyPositions: healthy };
  }, [activePositions]);

  // Colour only for meaning: signed P&L reads gain/loss.
  const getPnLClass = (v: number | null | undefined) => {
    if (v == null || isNaN(v) || v === 0) return 'db-zero';
    return v > 0 ? 'db-gain' : 'db-loss';
  };

  const getTotalPnl = (pos: Position) => pos.totalPnl ?? null;   // glossary position_total_pnl
  const getDailyPnl = (pos: Position) => pos.todayPnl ?? null;   // glossary today_pnl
  const fmt = (v: number | null) => (v == null ? '—' : formatSignedCurrency(v));

  const renderAlertRow = (pos: Position) => {
    const totalPnl = getTotalPnl(pos);
    const dailyPnl = getDailyPnl(pos);
    const display = getAlertDisplay(pos);
    const erWarning = getEarningsWarning(pos.nextEarningsDate, pos.earningsInside);

    return (
      <div
        key={pos.id}
        className="glance-row glance-row-alert"
        onClick={() => navigate(`/portfolio/positions?ticker=${pos.ticker}`)}
      >
        {/* Rule fired = signal dot; earnings-only = warning dot */}
        {display ? <RuleGlyph progress={null} fired title="A rule fired" /> : <span className="glance-alert-dot is-warn" />}
        <div className="glance-row-info">
          <span className="glance-ticker">{pos.ticker}</span>
          <span className="glance-contract">
            ${pos.strikePrice.toFixed(0)} {formatExpiry(pos.expiryDate, pos.daysToExpiration)}
          </span>
          {erWarning && (
            <span className={`glance-er-tag${erWarning.daysUntil <= 1 ? ' is-imminent' : ''}`}>
              {erWarning.label}
            </span>
          )}
        </div>
        {display ? (
          <span className="glance-status">{display.label}</span>
        ) : (
          <span className="glance-status is-warn">{erWarning?.label}</span>
        )}
        <span className={`glance-daily ${getPnLClass(dailyPnl)}`}>
          {fmt(dailyPnl)}
        </span>
        <span className={`glance-pnl ${getPnLClass(totalPnl)}`}>
          {fmt(totalPnl)}
        </span>
      </div>
    );
  };

  const renderHealthyRow = (pos: Position) => {
    const totalPnl = getTotalPnl(pos);
    const dailyPnl = getDailyPnl(pos);
    const erWarning = getEarningsWarning(pos.nextEarningsDate, pos.earningsInside);

    return (
      <div
        key={pos.id}
        className="glance-row"
        onClick={() => navigate(`/portfolio/positions?ticker=${pos.ticker}`)}
      >
        <RuleGlyph progress={null} fired={false} />
        <div className="glance-row-info">
          <span className="glance-ticker">{pos.ticker}</span>
          <span className="glance-contract">
            ${pos.strikePrice.toFixed(0)} {formatExpiry(pos.expiryDate, pos.daysToExpiration)}
          </span>
          {erWarning && (
            <span className={`glance-er-tag${erWarning.daysUntil <= 1 ? ' is-imminent' : ''}`}>
              {erWarning.label}
            </span>
          )}
        </div>
        <span className={`glance-daily ${getPnLClass(dailyPnl)}`}>
          {fmt(dailyPnl)}
        </span>
        <span className={`glance-pnl ${getPnLClass(totalPnl)}`}>
          {fmt(totalPnl)}
        </span>
      </div>
    );
  };

  return (
    <div className="portfolio-glance command-tile">
      <div className="command-tile-header">
        <span className="command-tile-title">Portfolio at a Glance</span>
        <span className="glance-count">
          {activePositions.length} position{activePositions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {loading ? (
        <div className="glance-empty">Loading positions...</div>
      ) : activePositions.length === 0 ? (
        <div className="glance-empty">
          <div className="glance-empty-text">No active positions</div>
          <button className="glance-cta-btn" onClick={onNavigateToScreener}>
            Find Stocks to Trade
          </button>
        </div>
      ) : (
        <div className="glance-list">
          {/* Alert positions first */}
          {alertPositions.length > 0 && (
            <>
              <div className="glance-section-header">
                <span className="glance-section-label">Needs Attention</span>
                <span className="glance-section-cols glance-section-cols-3">
                  <span>Status</span>
                  <span>Daily</span>
                  <span>Total</span>
                </span>
              </div>
              {alertPositions.map(renderAlertRow)}
              {healthyPositions.length > 0 && (
                <div className="glance-divider" />
              )}
            </>
          )}

          {/* Healthy positions */}
          {healthyPositions.length > 0 && (
            <>
              <div className="glance-section-header">
                <span className="glance-section-label">On Track</span>
                <span className="glance-section-cols">
                  <span>Daily</span>
                  <span>Total</span>
                </span>
              </div>
              {healthyPositions.map(renderHealthyRow)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PortfolioGlance;
